import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { FileItem, FileLikeObject, FileUploader } from 'ng2-file-upload';
import { finalize } from 'rxjs/operators';
import { AttributeEditorMode } from '../../models/attributes/attributeEditorMode';
import { AppConfigService } from '../../services/app-config.service';
import { AWSService } from '../../services/aws.service';
import { CustomDocumentService } from '../../services/custom-document.service';
import { ModalConfirmationComponent } from '../modal-confirmation/modal-confirmation.component';
import { CustomDocumentUploadFile } from './custom-document-upload-file';
import { ApiVersions } from '../../services/api-version';
import { buildFileUploader, getRawFile, oneMb } from '../document-upload/document-upload.helper';

const maxFileSizeInMb = 10;
const allowedFileTypes = ['image', 'doc', 'xls', 'ppt', 'pdf'];

@Component({
  selector: 'app-custom-document-uploader',
  templateUrl: './custom-document-uploader.component.html',
  styleUrls: ['./custom-document-uploader.component.scss'],
})
export class CustomDocumentUploaderComponent implements OnInit {
  hasDropZoneOver: boolean = false;
  uploader: FileUploader;

  files: CustomDocumentUploadFile[] = [];

  @Output() uploadedFilesChanged = new EventEmitter<CustomDocumentUploadFile[]>();

  @Output() uploadStatusChanged = new EventEmitter<boolean>();
  @Output() downLoadFile = new EventEmitter<string>();
  @Input() mode: AttributeEditorMode = AttributeEditorMode.Application;

  @Input() uploadPath: { path: string; apiVersion: ApiVersions } = null;

  @Input() errorMessage: string = '';

  @Input() isTouched: boolean = false;

  @Input() required: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() entityId: string = '';

  @Input()
  set previouslyUploadedFiles(value: string) {
    let oldFiles = !value
      ? []
      : value.split('|').map((fileName) => {
          return new CustomDocumentUploadFile(fileName.trim(), '', null, false, false);
        });
    oldFiles.forEach((oldFile) => {
      if (!this.files.find((x) => x.fileName == oldFile.fileName)) {
        this.files.push(oldFile);
      }
    });
  }

  get previewMode(): boolean {
    return this.mode == AttributeEditorMode.Preview;
  }
  get personDetailsMode(): boolean {
    return this.mode == AttributeEditorMode.PersonDetails;
  }
  get isTeamDetailsMode(): boolean {
    return this.mode == AttributeEditorMode.TeamDetails;
  }
  constructor(
    private awsService: AWSService,
    private customDocumentService: CustomDocumentService,
    private dialog: MatDialog,
    private appConfigService: AppConfigService
  ) {}

  ngOnInit(): void {
    this.setupUploader();
  }

  public fileOver(e: any) {
    this.hasDropZoneOver = e;
  }

  get doesFormHaveError(): boolean {
    return (
      this.required &&
      this.isTouched &&
      this.files.filter((x) => !x.hasError && !x.isUploading).length == 0
    );
  }

  removeFileFromList(file: CustomDocumentUploadFile) {
    let removeIndex = this.files.indexOf(file);
    if (removeIndex > -1) {
      this.files.splice(removeIndex, 1);
    }

    if (!file.hasError) {
      this.filesChanged();
      return;
    }
  }

  onDownloadFile(file: CustomDocumentUploadFile) {
    this.downLoadFile.emit(file.fileName);
  }

  deleteFile(file: CustomDocumentUploadFile) {
    if (this.personDetailsMode || this.isTeamDetailsMode) {
      if (file.hasError) {
        this.removeFileFromList(file);
        return;
      }
      let confirmationDialogRef = this.dialog.open(ModalConfirmationComponent, {
        data: { action: `delete ${file.fileName}`, delete: true },
      });

      confirmationDialogRef.afterClosed().subscribe((result) => {
        if (result) {
          let filePath = this.getDeleteFilePath(file);

          this.awsService
            .deleteFromS3(this.entityId, filePath, this.isTeamDetailsMode)
            .subscribe(() => {
              this.removeFileFromList(file);
              this.awsService.openSnackBar(`Successfully deleted ${file.fileName}`, true);
            });
        }
      });
    } else {
      this.removeFileFromList(file);
    }
  }

  deletePersonDocument(file: CustomDocumentUploadFile) {}

  private getDeleteFilePath(file: CustomDocumentUploadFile) {
    let tenantId = this.appConfigService.tenant.id;
    if (this.personDetailsMode) {
      return this.uploadPath.path
        .replace('user', `${tenantId}/person-documents`)
        .replace('custom-fields/', '')
        .replace('custom-document', file.fileName);
    }
    if (this.isTeamDetailsMode) {
      return this.uploadPath.path
        .replace('teams', `${tenantId}/team-documents`)
        .replace('custom-document', file.fileName);
    }
  }

  private filesChanged() {
    this.uploadedFilesChanged.emit(this.files.filter((x) => !x.hasError && !x.isUploading));
  }
  private setupUploader() {
    this.uploader = buildFileUploader({
      maxFileSizeInMb: maxFileSizeInMb,
      allowedFileType: allowedFileTypes,
    });

    this.uploader.onAfterAddingAll = (fileItems: FileItem[]) => {
      let uploadFiles = fileItems.map((fileItem) => {
        let file = new CustomDocumentUploadFile(
          fileItem.file.name.trim(),
          '',
          getRawFile(fileItem),
          true,
          !this.previewMode
        );
        let index = this.files.findIndex((x) => x.fileName == fileItem.file.name.trim());
        if (index != -1) {
          file.errorMessage = 'File by this name already exists. It was not uploaded.';
          file.rawFile = null;
          file.isUploading = false;
        }
        this.files.push(file);
        return file;
      });
      if (this.previewMode) {
        return;
      }

      uploadFiles
        .filter((file) => !file.hasError)
        .forEach((item) => {
          this.customDocumentService
            .uploadDocument(item, this.uploadPath.path, this.uploadPath.apiVersion)
            .pipe(
              finalize(() => {
                this.filesChanged();
                let isUploading = this.files.find((x) => x.isUploading) != null;
                this.uploadStatusChanged.emit(isUploading);
              })
            )
            .subscribe(
              (response: CustomDocumentUploadFile) => {
                let fileIndex = this.files.findIndex((x) => x.fileName === response.fileName);
                if (fileIndex != -1) {
                  this.files[fileIndex] = response;
                }
              },
              (err: HttpErrorResponse) => {
                let fileIndex = this.files.findIndex((x) => x.fileName === item.fileName);
                if (fileIndex != -1) {
                  this.files[fileIndex].errorMessage =
                    err.error && err.error.length > 0 ? err.error[0] : err.message;
                  this.files[fileIndex].isUploading = false;
                }
              }
            );
        });
    };
    this.uploader.onWhenAddingFileFailed = (item, filter, options) =>
      this.onWhenAddingFileFailed(item, filter, options);
  }

  private onWhenAddingFileFailed(item: FileLikeObject, filter: any, options: any) {
    let errorMessage = '';

    switch (filter.name) {
      case 'fileSize':
        let fileSize = Math.round((item.size / oneMb) * 100) / 100;
        errorMessage = `Maximum upload size exceeded (${fileSize} MB of ${maxFileSizeInMb} MB allowed)`;
        break;
      case 'fileType':
        errorMessage = 'File type is not supported.';
        break;
      default:
        errorMessage = 'This file cannot be uploaded, please try again with a different file.';
    }
    let index = this.files.findIndex((x) => x.fileName == item.name.trim());
    if (index == -1) {
      let file = new CustomDocumentUploadFile(item.name.trim(), errorMessage, null, true, false);
      this.files.push(file);
    }
  }
}
